import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useCreateDispatch } from '@/api/dispatches'
import type { DispatchBody } from '@/api/dispatches'
import { useIdempotencyKey } from '@/api/idempotency'
import { useSalesChannels } from '@/api/sales-channels'
import { useAllStock } from '@/api/stock'
import { useWorkspace } from '@/api/workspace'
import { blockingItemsOf } from '@/components/corrections/blocking-items'
import { dispatchErrorMessage, needsBackdateReason } from '@/components/dispatches/error-message'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatQty } from '@/components/materials/pool-format'
import { SearchCombobox } from '@/components/search-combobox'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Create a Dispatch (UI-SCREENS I-05, 05c #171). Phone first: one card per line, 44px targets,
// numeric keyboards. Each line shows the Variant's current Home stock, and a quantity above it is
// refused here and, under the balance lock, by the server. `?channelId=` prefills the channel, so
// the Dispatch action on the Sales Channel screen is one tap away.
const searchSchema = z.object({
  channelId: z.coerce.number().int().positive().optional(),
})

export const Route = createFileRoute('/w/$slug/dispatches/new')({
  component: NewDispatch,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'dispatches.form.title' },
})

// A Variant that can be shipped, with what Home holds right now (the Stock view's figures).
type Item = { id: number; sku: string; name: string; home: number }

function NewDispatch() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const { channelId } = Route.useSearch()
  const workspace = useWorkspace(slug)
  // Archived channels take no new Dispatches (#169 Q2), so only the active ones are offered.
  const channels = useSalesChannels(slug)
  const stock = useAllStock(slug)
  const items: Item[] | undefined = stock.data?.items.map((row) => ({
    id: row.variant.id,
    sku: row.variant.sku,
    name: row.variant.name,
    home: row.stock.home,
  }))
  const activeChannels = (channels.data ?? []).filter((channel) => !channel.archived_at)

  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <Link to="/w/$slug/dispatches" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('dispatches.back')}
      </Link>
      <h1 className="font-heading text-xl font-semibold">{t('dispatches.form.title')}</h1>

      {workspace.isPending || channels.isPending || stock.isPending ? (
        <LoadingSkeleton variant="form" rows={4} />
      ) : workspace.isError || !workspace.data || channels.isError || stock.isError || !items ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : !workspace.data.adopted_on ? (
        <Missing message={t('dispatches.errors.adoptionDateNotSet')} />
      ) : activeChannels.length === 0 ? (
        <Missing message={t('dispatches.form.noChannels')} />
      ) : items.length === 0 ? (
        <Missing message={t('dispatches.form.noVariants')} />
      ) : (
        <DispatchForm
          slug={slug}
          adoptedOn={workspace.data.adopted_on}
          timezone={workspace.data.timezone}
          channels={activeChannels.map((channel) => ({ id: channel.id, name: channel.name }))}
          items={items}
          prefilledChannelId={activeChannels.some((channel) => channel.id === channelId) ? channelId : undefined}
        />
      )}
    </section>
  )
}

function Missing({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="py-4">
        <p role="status" className="text-sm">
          {message}
        </p>
      </CardContent>
    </Card>
  )
}

const EMPTY_LINE = { variantId: '', qty: '' }

// Today in the Workspace's timezone: the business date may never be after it (domain-rules).
function todayIn(timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date())
}

function DispatchForm({
  slug,
  adoptedOn,
  timezone,
  channels,
  items,
  prefilledChannelId,
}: {
  slug: string
  adoptedOn: string
  timezone: string
  channels: { id: number; name: string }[]
  items: Item[]
  prefilledChannelId?: number
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const create = useCreateDispatch()
  const idempotency = useIdempotencyKey()
  // The backdate limit lives on the server; the reason field appears once it asks for one.
  const [askReason, setAskReason] = useState(false)
  const today = todayIn(timezone)

  // Every stored number is an integer (domain-rules); Home never goes negative, so a quantity
  // above what Home holds is refused here too — the server re-checks it under the balance lock.
  const formSchema = z
    .object({
      channelId: z.string().min(1, { message: t('dispatches.form.required') }),
      occurredOn: z.string().min(1, { message: t('dispatches.form.required') }),
      note: z.string(),
      backdateReason: z.string(),
      lines: z
        .array(z.object({ variantId: z.string().min(1, { message: t('dispatches.form.required') }), qty: z.string() }))
        .min(1),
    })
    .superRefine((values, context) => {
      values.lines.forEach((line, index) => {
        if (!/^\d+$/.test(line.qty) || Number(line.qty) < 1) {
          context.addIssue({ code: 'custom', path: ['lines', index, 'qty'], message: t('dispatches.form.qtyInvalid') })
          return
        }
        const item = items.find((candidate) => String(candidate.id) === line.variantId)
        if (item && Number(line.qty) > item.home) {
          context.addIssue({
            code: 'custom',
            path: ['lines', index, 'qty'],
            message: t('dispatches.form.aboveHome', { qty: formatQty(item.home) }),
          })
        }
      })
    })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      channelId: prefilledChannelId ? String(prefilledChannelId) : String(channels[0].id),
      occurredOn: today,
      note: '',
      backdateReason: '',
      lines: [EMPTY_LINE],
    },
  })
  const lines = useFieldArray({ control: form.control, name: 'lines' })
  // useWatch rather than form.watch: it returns a value the React compiler can memoize.
  const watched = useWatch({ control: form.control, name: 'lines' })
  // The Variant the server refused first, if Home was too low (05e #173).
  const blockedItemId = create.isError ? blockingItemsOf(create.error)[0]?.stock_item_id : undefined

  const onSubmit = form.handleSubmit((values) => {
    const backdateReason = values.backdateReason.trim()
    const body: DispatchBody = {
      dispatch: {
        sales_channel_id: Number(values.channelId),
        occurred_on: values.occurredOn,
        note: values.note.trim() === '' ? null : values.note.trim(),
        ...(backdateReason === '' ? {} : { backdate_reason: backdateReason }),
        lines: values.lines.map((line) => ({ variant_id: Number(line.variantId), qty: Number(line.qty) })),
      },
    }
    create.mutate(
      { params: { path: { slug }, header: idempotency.headerFor(body) }, body },
      {
        onSuccess: (data) =>
          navigate({ to: '/w/$slug/dispatches/$dispatchId', params: { slug, dispatchId: String(data.id) } }),
        onSettled: (_data, error) => {
          idempotency.settle(error)
          if (needsBackdateReason(error)) setAskReason(true)
        },
      },
    )
  })

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {create.isError ? dispatchErrorMessage(t, create.error) : ''}
      </p>
      {/* Home stock too low: the count that fixes it is one tap away, at Home with the short
          Variant prefilled (I-11, 05e #173), as F-10 does it for WIP. The first blocking item
          opens the form; the rest are named in the message above. */}
      {blockedItemId ? (
        <Button asChild variant="outline" className="h-11 self-start">
          <Link
            to="/w/$slug/adjustments/new"
            params={{ slug }}
            search={{ location: 'home', itemId: blockedItemId }}
          >
            {t('dispatches.form.toAdjustment')}
          </Link>
        </Button>
      ) : null}

      <div className="flex flex-col gap-2">
        <Label htmlFor="channelId">{t('dispatches.form.channel')}</Label>
        <NativeSelect id="channelId" {...form.register('channelId')}>
          {channels.map((channel) => (
            <option key={channel.id} value={String(channel.id)}>
              {channel.name}
            </option>
          ))}
        </NativeSelect>
        <FieldError message={form.formState.errors.channelId?.message} />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="occurredOn">{t('dispatches.form.date')}</Label>
        <Input
          id="occurredOn"
          type="date"
          className="h-11"
          min={adoptedOn}
          max={today}
          {...form.register('occurredOn')}
        />
        <p className="text-sm text-muted-foreground">{t('dispatches.form.dateHint')}</p>
        <FieldError message={form.formState.errors.occurredOn?.message} />
      </div>

      {askReason ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="backdateReason">{t('dispatches.form.backdateReason')}</Label>
          <Input id="backdateReason" className="h-11" {...form.register('backdateReason')} />
          <p className="text-sm text-muted-foreground">{t('dispatches.form.backdateReasonHint')}</p>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        {lines.fields.map((field, index) => (
          <Card key={field.id}>
            <CardContent className="flex flex-col gap-3 py-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor={`variant-${index}`}>{t('dispatches.form.variant')}</Label>
                <Controller
                  control={form.control}
                  name={`lines.${index}.variantId` as const}
                  render={({ field, fieldState }) => (
                    <SearchCombobox
                      id={`variant-${index}`}
                      value={field.value}
                      options={items.map((item) => ({ value: String(item.id), code: item.sku, name: item.name }))}
                      invalid={Boolean(fieldState.error)}
                      placeholder={t('dispatches.form.selectVariant')}
                      searchPlaceholder={t('searchCombobox.variant.search')}
                      onChange={field.onChange}
                    />
                  )}
                />
                <FieldError message={form.formState.errors.lines?.[index]?.variantId?.message} />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor={`qty-${index}`}>{t('dispatches.form.qty')}</Label>
                <Input id={`qty-${index}`} inputMode="numeric" className="h-11" {...form.register(`lines.${index}.qty`)} />
                <p className="text-sm text-muted-foreground">
                  {t('dispatches.form.homeStock', { qty: formatQty(homeOf(watched[index], items)) })}
                </p>
                <FieldError message={form.formState.errors.lines?.[index]?.qty?.message} />
              </div>

              {lines.fields.length > 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="h-11 self-start text-destructive"
                  onClick={() => lines.remove(index)}
                >
                  <Trash2 aria-hidden="true" />
                  {t('dispatches.form.removeLine')}
                </Button>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>

      <Button type="button" variant="outline" className="h-11 self-start" onClick={() => lines.append(EMPTY_LINE)}>
        {t('dispatches.form.addLine')}
      </Button>

      <div className="flex flex-col gap-2">
        <Label htmlFor="note">{t('dispatches.form.note')}</Label>
        <Input id="note" className="h-11" {...form.register('note')} />
        <FieldError message={form.formState.errors.note?.message} />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="h-11" disabled={create.isPending}>
          {t(create.isPending ? 'dispatches.form.saving' : 'dispatches.form.save')}
        </Button>
        <Button asChild variant="ghost" className="h-11">
          <Link to="/w/$slug/dispatches" params={{ slug }}>
            {t('dispatches.form.cancel')}
          </Link>
        </Button>
      </div>
    </form>
  )
}

const homeOf = (line: { variantId: string } | undefined, items: Item[]) =>
  items.find((item) => String(item.id) === line?.variantId)?.home ?? 0

function NativeSelect({ className, ...props }: React.ComponentProps<'select'>) {
  return (
    <select
      className={`h-11 w-full rounded-lg border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 ${className ?? ''}`}
      {...props}
    />
  )
}

function FieldError({ message }: { message?: string }) {
  return (
    <p role="alert" className="text-sm text-destructive empty:hidden">
      {message ?? ''}
    </p>
  )
}
